/**
 * Parse filename to extract bank and person metadata
 * Supports patterns like:
 * - bank_person_account.csv (e.g., chase_reece_sapphire.csv)
 * - bank_person.csv (e.g., amex_shelly.csv)
 * - bank.csv (e.g., chase.csv)
 */

const KNOWN_BANKS = [
  'amex',
  'american express',
  'bmo',
  'chase',
  'fidelity',
  'sofi',
  'usbank',
  'us bank',
  'wellsfargo',
  'wells fargo',
  'bofa',
  'bankofamerica',
  'bank of america',
  'citi',
  'citibank',
  'discover',
  'capital one',
  'capitalone'
];

export type ParsedMetadata = {
  bank?: string;
  person?: string;
  account?: string;
  confidence: 'high' | 'medium' | 'low';
};

/**
 * Parse a filename to extract metadata
 */
export function parseFilename(filename: string): ParsedMetadata {
  // Remove .csv extension and convert to lowercase for matching
  const nameWithoutExt = filename.replace(/\.csv$/i, '');
  const parts = nameWithoutExt.split(/[-_\s]+/); // Split on dash, underscore, or space

  let bank: string | undefined;
  let person: string | undefined;
  let account: string | undefined;
  let confidence: 'high' | 'medium' | 'low' = 'low';

  // Strategy 1: Try pattern bank_person_account or bank_person
  if (parts.length >= 2) {
    const firstPart = parts[0].toLowerCase();

    // Check if first part is a known bank
    const matchedBank = KNOWN_BANKS.find(b => firstPart.includes(b.replace(/\s+/g, '')));

    if (matchedBank) {
      bank = parts[0]; // Use original casing
      person = parts[1]; // Second part might be person or account type

      if (parts.length >= 3) {
        // Third part is account name
        account = parts.slice(2).join(' '); // Join remaining parts
        confidence = 'high';
      } else {
        // Only 2 parts - could be bank_person OR bank_cardtype
        // Lower confidence since it's ambiguous (e.g., "Amex - Blue" could be card type)
        confidence = 'medium';
      }
    } else {
      // Check if any part contains a known bank
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i].toLowerCase();
        const matched = KNOWN_BANKS.find(b => part.includes(b.replace(/\s+/g, '')));

        if (matched) {
          bank = parts[i];

          // If bank is not first, assume pattern is different
          if (i === 1 && parts.length >= 2) {
            // Pattern might be person_bank or person_bank_account
            person = parts[0];
            if (parts.length >= 3) {
              account = parts.slice(2).join(' ');
            }
            confidence = 'medium';
          } else if (i === 0 && parts.length >= 2) {
            // Pattern is bank_something
            person = parts[1];
            if (parts.length >= 3) {
              account = parts.slice(2).join(' ');
            }
            confidence = 'medium';
          }
          break;
        }
      }
    }
  } else if (parts.length === 1) {
    // Only one part - check if it's a known bank
    const part = parts[0].toLowerCase();
    const matchedBank = KNOWN_BANKS.find(b => part.includes(b.replace(/\s+/g, '')));

    if (matchedBank) {
      bank = parts[0];
      confidence = 'medium'; // Medium because we don't have person
    }
  }

  return {
    bank,
    person,
    account,
    confidence
  };
}

/**
 * Check if we should prompt user for metadata
 * Prompt if confidence is low/medium or if critical fields are missing
 */
export function shouldPromptForMetadata(parsed: ParsedMetadata): boolean {
  // Always prompt if confidence is low or medium
  if (parsed.confidence === 'low' || parsed.confidence === 'medium') {
    return true;
  }

  // Prompt if we have no bank (critical field)
  if (!parsed.bank) {
    return true;
  }

  // Don't prompt if we have bank and confidence is high
  return false;
}

/**
 * Format metadata for display in prompt
 */
export function formatMetadataForPrompt(parsed: ParsedMetadata): string {
  const parts: string[] = [];

  if (parsed.bank) parts.push(`Bank: ${parsed.bank}`);
  if (parsed.person) parts.push(`Person: ${parsed.person}`);
  if (parsed.account) parts.push(`Account: ${parsed.account}`);

  return parts.join(', ') || 'Could not parse filename';
}
